import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, Calendar as CalIcon, Tag, Users, DollarSign, ExternalLink, FileText, Save } from "lucide-react";
import OrganizerBadge from "@/components/OrganizerBadge";
import SafetyBanner from "@/components/SafetyBanner";
import ReportEventDialog from "@/components/ReportEventDialog";
import { RecapGallery } from "@/components/RecapPhotos";
import ApprovedVendorsRoster from "@/components/ApprovedVendorsRoster";
import PublicPredictionsRow from "@/components/PublicPredictionsRow";
import SocialLinkPill from "@/components/SocialLinkPill";
import AISuggestedVendors from "@/components/AISuggestedVendors";
import AIContractButton from "@/components/AIContractButton";
import OrganizerProGate from "@/components/OrganizerProGate";
import FundraiserPanel from "@/components/FundraiserPanel";
import VolunteerHostPanel from "@/components/VolunteerHostPanel";
import FollowButton from "@/components/FollowButton";

export default function EventDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [pitch, setPitch] = useState("");
  const [products, setProducts] = useState("");
  const [booth, setBooth] = useState("standard");
  const [busy, setBusy] = useState(false);
  // For recurring events: which occurrence dates the vendor is applying for.
  // Defaults to all when the dialog opens; user can uncheck any they can't attend.
  const [pickedDates, setPickedDates] = useState([]);
  // Application templates picker
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");

  useEffect(() => {
    api.get(`/events/${id}`).then(({ data }) => setEvent(data)).finally(() => setLoading(false));
  }, [id]);

  // Load templates the first time the vendor opens the apply dialog.
  useEffect(() => {
    if (!open || !user || user.role !== "vendor") return;
    if (templates.length > 0) return;
    api.get("/vendor/application-templates")
      .then(({ data }) => {
        setTemplates(data || []);
        const def = (data || []).find((t) => t.is_default);
        if (def) {
          setSelectedTemplateId(def.id);
          setPitch(def.pitch);
          setProducts(def.products);
          setBooth(def.booth_size || "standard");
        }
      })
      .catch(() => { /* silent — form still works empty */ });
  }, [open, user, templates.length]);

  const applyTemplate = (tplId) => {
    setSelectedTemplateId(tplId);
    if (!tplId) return; // "None" — leave fields as-is
    const t = templates.find((x) => x.id === tplId);
    if (!t) return;
    setPitch(t.pitch);
    setProducts(t.products);
    setBooth(t.booth_size || "standard");
  };

  // When the apply dialog opens on a recurring event, seed the picker
  // with all occurrences selected — a vendor who taps "Apply" is likely
  // saying yes to the whole run, but can uncheck any dates they can't do.
  useEffect(() => {
    if (open && event?.is_recurring) {
      setPickedDates(event.occurrences || []);
    }
  }, [open, event]);

  const toggleOccurrence = (d) => {
    setPickedDates((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort()
    );
  };

  const submitApp = async (e) => {
    e.preventDefault();
    if (event?.is_recurring && pickedDates.length === 0) {
      toast.error("Pick at least one date you can attend");
      return;
    }
    setBusy(true);
    try {
      const body = { event_id: id, pitch, products, booth_size: booth };
      if (event?.is_recurring) body.occurrence_dates = pickedDates;
      await api.post("/applications", body);
      // Optionally save this filled form as a new template
      if (saveAsTemplate && newTemplateName.trim()) {
        try {
          await api.post("/vendor/application-templates", {
            name: newTemplateName.trim(),
            pitch, products, booth_size: booth,
          });
          toast.success(`Application sent · Saved template "${newTemplateName.trim()}"`);
        } catch (tplErr) {
          // Application still submitted; just tell them why the template didn't save.
          toast.warning(`Application sent, but couldn't save template: ${formatApiErrorDetail(tplErr.response?.data?.detail) || tplErr.message}`);
        }
      } else {
        toast.success("Application submitted");
      }
      setOpen(false);
      navigate("/vendor/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to apply");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-20 text-center font-serif text-2xl">Loading…</div>;
  if (!event) return <div className="p-20 text-center font-serif text-2xl">Event not found</div>;

  const canApply = user && user.role === "vendor";

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-14">
      <div className="grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <div className="aspect-[16/9] overflow-hidden rounded-sm bg-[color:var(--sand)]">
            {event.cover_image ? (
              <img src={event.cover_image} alt={event.title} className="w-full h-full object-cover" data-testid="event-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center font-serif text-6xl" style={{ color: "var(--terracotta)" }}>{event.title.slice(0, 1)}</div>
            )}
          </div>
          <div className="overline mt-8" style={{ color: "var(--terracotta)" }}>{event.category}</div>
          <h1 className="font-serif text-5xl mt-2" data-testid="event-title">{event.title}</h1>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <OrganizerBadge
              verified={!!event.organizer_verified}
              kycApproved={!!event.organizer_kyc_approved}
            />
            {event.venue_url && (
              <a
                href={event.venue_url}
                target="_blank"
                rel="noreferrer"
                data-testid="event-venue-link"
                className="inline-flex items-center gap-1 text-xs uppercase tracking-widest hover:opacity-70"
                style={{ color: "var(--ink-soft)" }}
              >
                <ExternalLink className="h-3 w-3" /> Official venue page
              </a>
            )}
          </div>
          <p className="mt-6 text-base leading-relaxed" style={{ color: "var(--ink-soft)" }} data-testid="event-description">{event.description}</p>
          <FundraiserPanel event={event} />
          {event.social_url && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="event-social-row">
              <SocialLinkPill url={event.social_url} testId="event-social-link" />
            </div>
          )}
          {event.tags && event.tags.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {event.tags.map((t) => (
                <span key={t} className="px-3 py-1 rounded-full text-xs" style={{ backgroundColor: "var(--sand)", color: "var(--ink-soft)" }}>{t}</span>
              ))}
            </div>
          )}
          <div className="mt-8"><SafetyBanner /></div>
          <PublicPredictionsRow eventId={event.id} />
          <div className="mt-4 flex justify-end">
            {user ? <ReportEventDialog eventId={event.id} /> : (
              <span className="text-xs" style={{ color: "var(--ink-soft)" }}>Log in to report this event.</span>
            )}
          </div>
          <RecapGallery photos={event.recap_photos} />
          <ApprovedVendorsRoster eventId={event.id} />
          {user && user.role === "organizer" && event.organizer_id === user.id && (
            <>
              <OrganizerProGate
                feature="AI booth contracts"
                pitch="Claude drafts a plain-English, printable booth agreement in seconds — customized per vendor."
                perks={[
                  "Auto-populated with each vendor's business name + email",
                  "Downloadable PDF with your event branding",
                  "Cached per (event, vendor) so re-generation is instant",
                ]}
                testIdKey="ai-contract"
              >
                <div className="mt-8 flex items-center gap-3 flex-wrap">
                  <AIContractButton eventId={event.id} />
                  <span className="text-xs" style={{ color: "var(--ink-soft)" }}>
                    Draft an AI-generated vendor agreement for this event
                  </span>
                </div>
              </OrganizerProGate>
              <OrganizerProGate
                feature="AI Vendor Matching"
                pitch="Claude scans every vendor on AddVendTour and picks the best fits for THIS event."
                perks={[
                  "Ranked shortlist with match scores + reasons",
                  "One-tap invite fires a personalized notification",
                  "Re-run any time — cache refreshes daily",
                ]}
                testIdKey="ai-vendors"
              >
                <AISuggestedVendors eventId={event.id} />
              </OrganizerProGate>
              <VolunteerHostPanel eventId={event.id} />
            </>
          )}
        </div>
        <aside className="card-warm p-8 rounded-sm h-fit sticky top-28">
          <div className="space-y-4">
            <Row icon={<CalIcon className="h-4 w-4" />} label={event.is_recurring ? "First date" : "Date"}
              value={formatEventDate(event.date, event.event_time, event.timezone) + (event.end_date ? ` → ${event.end_date}` : "")} />
            {event.is_recurring && (
              <div data-testid="event-occurrences">
                <div className="overline mb-2" style={{ color: "var(--ink-soft)" }}>
                  Series · {event.occurrences?.length || 0} dates
                </div>
                <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
                  {(event.occurrences || []).slice(0, 24).map((d) => (
                    <span key={d} data-testid={`occurrence-${d}`}
                      className="text-[11px] px-2 py-0.5 rounded"
                      style={{ backgroundColor: "var(--paper-warm)", border: "1px solid var(--sand)",
                               color: "var(--ink-soft)" }}>
                      {formatShortDate(d)}
                    </span>
                  ))}
                  {(event.occurrences || []).length > 24 && (
                    <span className="text-[11px]" style={{ color: "var(--ink-soft)" }}>
                      +{event.occurrences.length - 24} more
                    </span>
                  )}
                </div>
              </div>
            )}
            {event.timezone && (
              <Row label="Timezone" value={event.timezone.replace("_", " ")} />
            )}
            <Row icon={<MapPin className="h-4 w-4" />} label="Location" value={`${event.location}, ${event.city}`} />
            <Row icon={<Tag className="h-4 w-4" />} label="Category" value={event.category} />
            <Row icon={<Users className="h-4 w-4" />} label="Max vendors" value={String(event.max_vendors)} />
            <Row icon={<DollarSign className="h-4 w-4" />} label="Booth fee" value={event.booth_fee > 0 ? `$${event.booth_fee.toFixed(2)}` : "Free"} />
            <Row label="Organizer" value={event.organizer_name} />
            {event.organizer_id && (
              <div className="mt-3">
                <FollowButton
                  organizerId={event.organizer_id}
                  organizerName={event.organizer_name}
                  testId="event-detail-follow-organizer"
                />
              </div>
            )}
          </div>
          <div className="mt-8">
            {!user && <Link to="/login" data-testid="apply-login-prompt" className="btn-primary w-full inline-block text-center py-3 rounded-full">Log in to apply</Link>}
            {user && user.role === "organizer" && <div className="text-sm" style={{ color: "var(--ink-soft)" }}>Organizers can't apply to events.</div>}
            {canApply && (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="apply-event-button" className="btn-primary w-full py-6 rounded-full">Apply to this event</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle className="font-serif text-3xl">Apply · {event.title}</DialogTitle></DialogHeader>
                  <form onSubmit={submitApp} className="space-y-4 mt-4">
                    {templates.length > 0 && (
                      <div
                        data-testid="template-picker"
                        className="rounded-md border p-3"
                        style={{ backgroundColor: "rgba(111,125,85,0.06)", borderColor: "rgba(111,125,85,0.25)" }}
                      >
                        <Label className="text-xs uppercase tracking-widest flex items-center gap-1.5">
                          <FileText size={12} /> Autofill from a saved template
                        </Label>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <button
                            type="button"
                            data-testid="template-chip-none"
                            onClick={() => setSelectedTemplateId("")}
                            className="text-xs px-3 py-1.5 rounded-full border"
                            style={{
                              borderColor: selectedTemplateId === "" ? "var(--moss)" : "rgba(0,0,0,0.15)",
                              backgroundColor: selectedTemplateId === "" ? "rgba(111,125,85,0.15)" : "#fff",
                              color: selectedTemplateId === "" ? "var(--moss)" : "var(--ink)",
                              fontWeight: selectedTemplateId === "" ? 600 : 400,
                            }}
                          >
                            Start blank
                          </button>
                          {templates.map((t) => (
                            <button
                              key={t.id}
                              type="button"
                              data-testid={`template-chip-${t.id}`}
                              onClick={() => applyTemplate(t.id)}
                              className="text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1"
                              style={{
                                borderColor: selectedTemplateId === t.id ? "var(--moss)" : "rgba(0,0,0,0.15)",
                                backgroundColor: selectedTemplateId === t.id ? "rgba(111,125,85,0.15)" : "#fff",
                                color: selectedTemplateId === t.id ? "var(--moss)" : "var(--ink)",
                                fontWeight: selectedTemplateId === t.id ? 600 : 400,
                              }}
                            >
                              {t.is_default && <span style={{ color: "#c48b2e" }}>★</span>}
                              {t.name}
                            </button>
                          ))}
                        </div>
                        <div className="mt-2 text-[11px]" style={{ color: "var(--ink-soft)" }}>
                          Manage your templates on the{" "}
                          <Link to="/vendor/templates" className="underline">Templates page</Link>.
                        </div>
                      </div>
                    )}
                    {templates.length === 0 && user?.role === "vendor" && (
                      <div
                        data-testid="template-empty-hint"
                        className="text-xs rounded-md p-3"
                        style={{ backgroundColor: "var(--sand)", color: "var(--ink-soft)" }}
                      >
                        <FileText size={12} className="inline mr-1" />
                        Tip: save reusable pitches on your{" "}
                        <Link to="/vendor/templates" className="underline">Templates page</Link>{" "}
                        so you can autofill this form in one tap next time.
                      </div>
                    )}
                    <div>
                      <Label className="text-xs uppercase tracking-widest">Your pitch</Label>
                      <Textarea data-testid="apply-pitch" value={pitch} onChange={(e) => setPitch(e.target.value)} required rows={4} placeholder="Tell the organizer about your work…" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest">What you&apos;ll sell</Label>
                      <Textarea data-testid="apply-products" value={products} onChange={(e) => setProducts(e.target.value)} required rows={3} placeholder="Handmade ceramics, small batch candles…" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest">Booth size</Label>
                      <Input data-testid="apply-booth" value={booth} onChange={(e) => setBooth(e.target.value)} placeholder="standard / large / corner" />
                    </div>
                    {event.is_recurring && (
                      <div
                        data-testid="apply-occurrence-picker"
                        className="rounded-md border p-3"
                        style={{ backgroundColor: "rgba(111,125,85,0.06)", borderColor: "rgba(111,125,85,0.25)" }}
                      >
                        <Label className="text-xs uppercase tracking-widest flex items-center gap-1.5">
                          <CalIcon size={12} /> Which dates can you attend?
                        </Label>
                        <p className="text-[11px] mt-1" style={{ color: "var(--ink-soft)" }}>
                          Uncheck any you can&apos;t make — the organizer will only approve you for the checked dates.
                        </p>
                        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-56 overflow-y-auto">
                          {(event.occurrences || []).map((d) => {
                            const on = pickedDates.includes(d);
                            return (
                              <label
                                key={d}
                                data-testid={`apply-occurrence-${d}`}
                                className="flex items-center gap-2 px-2 py-1 rounded border cursor-pointer text-xs"
                                style={{
                                  backgroundColor: on ? "rgba(111,125,85,0.15)" : "#fff",
                                  borderColor: on ? "var(--moss)" : "rgba(0,0,0,0.12)",
                                  color: on ? "var(--moss)" : "var(--ink)",
                                  fontWeight: on ? 600 : 400,
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={on}
                                  onChange={() => toggleOccurrence(d)}
                                  className="h-3.5 w-3.5"
                                />
                                {formatShortDate(d)}
                              </label>
                            );
                          })}
                        </div>
                        <div className="mt-2 flex gap-2">
                          <button type="button" data-testid="apply-select-all"
                            onClick={() => setPickedDates(event.occurrences || [])}
                            className="text-[11px] underline" style={{ color: "var(--ink-soft)" }}>
                            Select all
                          </button>
                          <button type="button" data-testid="apply-clear-all"
                            onClick={() => setPickedDates([])}
                            className="text-[11px] underline" style={{ color: "var(--ink-soft)" }}>
                            Clear
                          </button>
                          <span className="text-[11px] ml-auto" style={{ color: "var(--ink-soft)" }}
                            data-testid="apply-occurrence-count">
                            {pickedDates.length}/{event.occurrences?.length || 0} selected
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="rounded-md border p-3" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid="apply-save-as-template"
                          checked={saveAsTemplate}
                          onChange={(e) => setSaveAsTemplate(e.target.checked)}
                          className="h-4 w-4"
                        />
                        <Save size={13} />
                        <span>Save this as a new template for next time</span>
                      </label>
                      {saveAsTemplate && (
                        <Input
                          data-testid="apply-new-template-name"
                          value={newTemplateName}
                          onChange={(e) => setNewTemplateName(e.target.value)}
                          placeholder="Template name (e.g. Farmers market pitch)"
                          maxLength={80}
                          className="mt-2"
                        />
                      )}
                    </div>
                    {event.booth_fee > 0 && (
                      <div className="text-xs p-3 rounded" style={{ backgroundColor: "var(--sand)", color: "var(--ink-soft)" }}>
                        Booth fee of ${event.booth_fee.toFixed(2)} is charged only after your application is approved.
                      </div>
                    )}
                    <Button type="submit" data-testid="apply-submit" disabled={busy} className="btn-primary w-full py-6 rounded-full">
                      {busy ? "Submitting…" : "Submit application"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function Row({ icon, label, value }) {
  return (
    <div>
      <div className="overline flex items-center gap-1.5" style={{ color: "var(--ink-soft)" }}>{icon}{label}</div>
      <div className="mt-1 text-sm font-medium">{value}</div>
    </div>
  );
}

function formatShortDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" });
  } catch {
    return iso;
  }
}

function formatEventDate(iso, time, tz) {
  try {
    const d = new Date(iso + (time ? `T${time}:00` : "T00:00:00"));
    const opts = { year: "numeric", month: "short", day: "numeric" };
    if (time) { opts.hour = "numeric"; opts.minute = "2-digit"; }
    if (time && tz) opts.timeZone = tz;
    return d.toLocaleString(undefined, opts) + (time && tz ? ` (${tz.replace("_", " ")})` : "");
  } catch {
    return iso;
  }
}
